import { useEffect, useState, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { WS_URL } from '../api/config';
import { alertIncomingMessage } from '../utils/messageAlerts';

/**
 * Одно websocket-соединение на всё приложение.
 *
 * Хук монтировался в трёх местах (App, список чатов, открытый чат), и каждый
 * экземпляр открывал собственный сокет со своим heartbeat — сервер видел
 * 2–3 подключения одного пользователя, а звук уведомления играл столько же
 * раз. Теперь соединение общее: компоненты только подписываются на события,
 * а `authenticate` и heartbeat живут здесь.
 */

let sharedSocket: Socket | null = null;
let sharedUserId: string | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

/** Подключиться (или вернуть уже открытое соединение) для пользователя. */
const getSharedSocket = (userId: string): Socket => {
    if (sharedSocket && sharedUserId === userId) return sharedSocket;

    // Сменился пользователь — прошлое соединение закрываем, иначе после
    // выхода из аккаунта оно продолжало получать чужие уведомления.
    disconnectWebSocket();

    sharedUserId = userId;
    const socket = io(WS_URL, {
        transports: ['websocket'],
        reconnection: true,
    });
    sharedSocket = socket;

    // Сервер ждёт объект `{ userId }`, строка тоже поддерживается — шлём
    // объект, чтобы формат был одинаковым на всех клиентах.
    const authenticate = () => socket.emit('authenticate', { userId });

    socket.on('connect', authenticate);

    // Одно уведомление (звук, вибрация, push) на входящее сообщение,
    // сколько бы компонентов ни слушали сокет.
    socket.on('newMessage', (payload) => alertIncomingMessage(payload, userId));

    heartbeatTimer = setInterval(() => {
        if (socket.connected) socket.emit('heartbeat', { userId });
    }, 30000);

    return socket;
};

/** Закрыть общее соединение (выход из аккаунта, смена пользователя). */
export const disconnectWebSocket = () => {
    if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
    }
    if (sharedSocket) {
        sharedSocket.removeAllListeners();
        sharedSocket.disconnect();
        sharedSocket = null;
    }
    sharedUserId = null;
};

export const useWebSocket = (conversationId?: string, userId?: string | number) => {
    const [latestMessage, setLatestMessage] = useState<any>(null);
    const socketRef = useRef<Socket | null>(null);

    useEffect(() => {
        if (!userId) {
            disconnectWebSocket();
            socketRef.current = null;
            return;
        }

        const socket = getSharedSocket(String(userId));
        socketRef.current = socket;

        // Все сообщения и события приходят в один канал `newMessage`.
        // Отдельный канал `user_*_messages` сервер больше не рассылает
        // всему миру — персональные пуши идут в комнату пользователя.
        const onNewMessage = (payload: any) => setLatestMessage(payload);
        const onStatusChange = (data: any) => setLatestMessage({ event: 'userStatusChanged', ...data });

        socket.on('newMessage', onNewMessage);
        socket.on('userStatusChanged', onStatusChange);

        const joinConversation = () => {
            if (conversationId) socket.emit('join_conversation', conversationId);
        };
        socket.on('connect', joinConversation);
        // Если сокет уже подключён (переключение между чатами) — входим сразу.
        if (socket.connected) joinConversation();

        return () => {
            socket.off('newMessage', onNewMessage);
            socket.off('userStatusChanged', onStatusChange);
            socket.off('connect', joinConversation);
        };
    }, [conversationId, userId]);

    return { latestMessage, socket: socketRef.current };
};
